import { useEffect } from 'react'

import type {
  AssignedWorkflow,
  PendingWorkflow,
  WorkflowAction,
} from '../../shared/hooks/useIncidentWorkflow'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useLightMode } from '@/hooks/useLightMode'

import { IncidentWorkflowStatus } from '@/graphql/generated/schemas'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import {
  closeWorkflowDrawer,
  selectWorkflowDrawerIsOpen,
} from '@/redux/ui/uiSlice'

import {
  INCIDENT_DRAWER_WIDTH,
  IncidentDrawerContainer,
} from '../../components/IncidentDrawerContainer'
import { WorkflowDrawerBody } from './WorkflowDrawerBody'
import { WorkflowDrawerHeader } from './WorkflowDrawerHeader'

export const WORKFLOW_DRAWER_WIDTH = 445
export const WORKFLOW_DRAWER_BREAKPOINT =
  INCIDENT_DRAWER_WIDTH + WORKFLOW_DRAWER_WIDTH

interface WorkflowDrawerIProps {
  assignedWorkflow: AssignedWorkflow | null
  currentStepId: string | null
  hasWorkflows: boolean
  incidentId: string | null
  isActiveOwner: boolean
  isLoadingWorkflow: boolean
  pendingWorkflow: PendingWorkflow | null
  pendingWorkflowFull: AssignedWorkflow | null
  workflowActions: WorkflowAction[]
  workflowStatus: IncidentWorkflowStatus | null
}

export const WorkflowDrawer = ({
  assignedWorkflow,
  currentStepId,
  hasWorkflows,
  incidentId,
  isActiveOwner,
  isLoadingWorkflow,
  pendingWorkflow,
  pendingWorkflowFull,
  workflowActions,
  workflowStatus,
}: WorkflowDrawerIProps) => {
  const isOpen = useAppSelector(selectWorkflowDrawerIsOpen)
  const dispatch = useAppDispatch()
  const isRebrand = useFeatureFlag(FeatureFlag.incidentDrawerRebrand)
  const mode = useLightMode()
  const isDark = mode === 'dark'
  const drawerBg =
    isRebrand && isDark ? 'var(--hw-dark-colors-bg-card, #00121b)' : 'white'

  // Close the drawer if the current incident no longer has workflows
  // (e.g. navigating to a different incident).
  useEffect(() => {
    if (isOpen && !hasWorkflows) {
      dispatch(closeWorkflowDrawer())
    }
  }, [hasWorkflows, isOpen])

  if (!isOpen || !hasWorkflows) return null

  return (
    <IncidentDrawerContainer
      bgColor={drawerBg}
      borderColor={
        isRebrand
          ? isDark
            ? 'var(--hw-dark-colors-border-subtle, #0c3145)'
            : 'var(--hw-dark-colors-light-border-subtle, #F1F5F9)'
          : undefined
      }
      dataTestid='WorkflowDrawer:wrapper'
      width={WORKFLOW_DRAWER_WIDTH}
    >
      <WorkflowDrawerHeader
        mode={mode}
        onClose={() => dispatch(closeWorkflowDrawer())}
      />
      <WorkflowDrawerBody
        assignedWorkflow={assignedWorkflow}
        currentStepId={currentStepId}
        incidentId={incidentId}
        isActiveOwner={isActiveOwner}
        isLoadingWorkflow={isLoadingWorkflow}
        pendingWorkflow={pendingWorkflow}
        pendingWorkflowFull={pendingWorkflowFull}
        workflowActions={workflowActions}
        workflowStatus={workflowStatus}
      />
    </IncidentDrawerContainer>
  )
}
